import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BookCover } from "../components/Bits";
import WaitlistDialog from "../components/Waitlist";

const PROBLEMS = [
  ["Write more, market less.", "Spend less time figuring out promotion and more time writing."],
  ["Reach your readers.", "Connect with readers through the AuthorOS shelf, book clubs and reviewers."],
  ["Your book. Your rights.", "Build your readership while keeping ownership of your work."],
];

const KIT = [
  ["Stand out", "Give readers a clear reason to choose your book."],
  ["Get found", "Help readers discover your book through relevant search terms and categories."],
  ["Win readers over", "Turn browsing into buying with a compelling book description and sales page."],
  ["Build your following", "Give readers an author they want to follow beyond one book."],
  ["Get featured", "Approach book clubs and reviewers with a compelling introduction to your book."],
  ["Build momentum", "One action a day, with content to share and clear direction."],
];

function Hero() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const start = () => navigate(user ? "/books/new" : "/signin");
  const [waitlist, setWaitlist] = useState(false);
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-7 rise">
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="landing-headline">
          You write the books.<br /><span className="scribble italic">AuthorOS</span> handles the rest.
        </h1>
        <p className="mt-8 text-lg leading-relaxed text-[#5C584E] max-w-xl" data-testid="landing-subline">
          Everything you need to launch and sell your book, in one place.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <button className="btn-primary" onClick={start} data-testid="landing-hero-cta-button">
            Start selling my book <ArrowRight size={14} />
          </button>
          <Link to="/discover" className="btn-secondary" data-testid="landing-browse-shelf-link">Browse the shelf</Link>
        </div>
        <button className="mt-5 text-[#5C584E] border-b border-[#8E8879] hover:text-[#C84B31] hover:border-[#C84B31] transition-colors" onClick={() => setWaitlist(true)} data-testid="landing-waitlist-link">
          Join the author waitlist for early access
        </button>
        <WaitlistDialog open={waitlist} onOpenChange={setWaitlist} />
      </div>
      <div className="lg:col-span-5 relative h-[430px] sm:h-[580px] rise" style={{ animationDelay: ".15s" }}>
        <BookCover src="/samples/cover2.jpg" title="Ash & Ivy" className="absolute w-32 sm:w-44 left-0 top-16 -rotate-6" />
        <BookCover src="/samples/cover1.jpg" title="The Lantern Keeper's Daughter" className="absolute w-36 sm:w-48 left-[30%] top-0 rotate-2 z-10" />
        <BookCover title="Your book, here" author="You" className="absolute w-32 sm:w-44 right-0 top-36 rotate-6 z-20" />
        <div className="absolute bottom-0 left-0 flex items-end gap-1 text-[#C84B31] z-30" data-testid="landing-hero-arrow">
          <p className="font-hand font-bold text-3xl sm:text-4xl -rotate-3 leading-none pb-1">Your book belongs here</p>
          <svg width="74" height="84" viewBox="0 0 74 84" fill="none" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 78 C 30 74, 52 58, 58 14" />
            <path d="M44 26 L 58 10 L 68 30" />
          </svg>
        </div>
      </div>
    </section>
  );
}

function Problems() {
  return (
    <section className="bg-[#121210] text-[#F5F2EA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <h2 className="font-display text-4xl leading-tight" data-testid="landing-outcome-heading">You've written the book.<br />Now build your readership.</h2>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-8">
          {PROBLEMS.map(([t, d], i) => (
            <div key={t} className="border-t border-[#2E2D28] pt-5" data-testid={`landing-problem-${i}`}>
              <p className="font-type text-xs text-[#E05A47]">0{i + 1}</p>
              <h3 className="font-display text-2xl mt-3 leading-snug">{t}</h3>
              <p className="mt-3 text-[#A39E93] text-[0.95rem] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KitList() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
        <p className="label-mono">Your book journey</p>
        <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">From finished book to finding readers.</h2>
        <p className="mt-6 text-[#5C584E] leading-relaxed max-w-md">Give your book the launch it deserves—and keep reaching readers after release.</p>
        <img src="/samples/desk.jpg" alt="Typewriter on a writer's desk" className="mt-10 w-full h-56 object-cover grayscale-[30%] border border-[#E5DEC9]" />
      </div>
      <ol className="lg:col-span-7">
        {KIT.map(([t, d], i) => (
          <li key={t} className="grid grid-cols-[3.5rem_1fr] gap-4 border-t border-[#E5DEC9] py-7 group" data-testid={`landing-kit-item-${i}`}>
            <span className="font-type text-sm text-[#C84B31] pt-1">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <h3 className="font-display text-2xl group-hover:italic transition-all">{t}</h3>
              <p className="mt-2 text-[#5C584E] leading-relaxed">{d}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ShelfTeaser() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-0 border border-[#121210]">
      <img src="/samples/shelf.jpg" alt="Indie bookshop shelf" className="lg:col-span-5 w-full h-72 lg:h-full object-cover" />
      <div className="lg:col-span-7 p-8 sm:p-14 bg-[#F6F2EA]">
        <h2 className="font-display text-4xl sm:text-5xl leading-tight" data-testid="landing-readers-heading">Stories worth finding, from authors worth following.</h2>
        <p className="mt-5 text-[#5C584E] leading-relaxed max-w-lg">
          Find your next favourite book. Discover independent stories by mood, trope and the authors you already love.
        </p>
        <Link to="/discover" className="btn-primary mt-8" data-testid="landing-discover-cta">Browse the shelf <ArrowRight size={14} /></Link>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <Problems />
      <KitList />
      <ShelfTeaser />
    </div>
  );
}
